import type { ComponentPropsWithRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn.ts';
import type { TypePillProps } from './type-pill.tsx';

/*
 * A pill's colour on its own, as a filled dot. The pill's ink rather than its
 * tint: a tint is a background, and at twelve pixels a 15%-alpha background is
 * not a colour anyone could name.
 */
const colorDotVariants = cva('inline-block shrink-0 rounded-full', {
  variants: {
    type: {
      oneOnOne: 'bg-type-oneonone',
      functionering: 'bg-type-func',
      disfunctionering: 'bg-type-dysfunc',
      reintegratie: 'bg-type-reint',
      selectie: 'bg-type-select',
      none: 'bg-type-none',
      journey: 'bg-on-pink',
    },
    size: {
      /* Beside a name in a list, where the name carries the row. */
      sm: 'size-2',
      md: 'size-3',
      lg: 'size-3.5',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

export type ColorDotProps = Omit<ComponentPropsWithRef<'span'>, 'style' | 'children'> &
  Omit<VariantProps<typeof colorDotVariants>, 'type'> & {
    type: TypePillProps['type'];
  };

/** Decorative: whatever the dot sits beside says in words what it is. */
export function ColorDot({ type, size, className, ...props }: ColorDotProps) {
  return (
    <span
      aria-hidden='true'
      data-slot='color-dot'
      className={cn(colorDotVariants({ type, size }), className)}
      {...props}
    />
  );
}
